/**
 * Produces a circular-cropped image blob from the source image and crop area.
 * Used for avatar/profile photos.
 */
export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function getCroppedImageBlob(
  imageUrl: string,
  cropArea: CropArea,
  circular = true,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const w = circular
          ? Math.min(cropArea.width, cropArea.height)
          : cropArea.width;
        const h = circular
          ? Math.min(cropArea.width, cropArea.height)
          : cropArea.height;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }
        if (circular) {
          ctx.beginPath();
          ctx.arc(w / 2, h / 2, w / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
        }
        ctx.drawImage(
          image,
          cropArea.x,
          cropArea.y,
          cropArea.width,
          cropArea.height,
          0,
          0,
          w,
          h,
        );
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create blob'));
          },
          'image/jpeg',
          0.92,
        );
      } catch (e) {
        reject(e);
      }
    };
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageUrl;
  });
}
